import { formatDistanceToNow } from 'date-fns';
import { ChevronRight, Pin, PinOff } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  useGetStaffNotesQuery,
  useTogglePinStaffNoteMutation,
} from '@/services/api/staffNotesService';
import { StaffNote } from '@/services/types/staff-notes-types';

const PINNED_BAR_HEIGHT_KEY = 'matter-pinned-notes-height';
const PINNED_BAR_MIN_HEIGHT = 52;
const PINNED_BAR_MAX_HEIGHT = 300;
const PINNED_BAR_DEFAULT_HEIGHT = 120;

// Compact pinned note item
const PinnedNoteItem: React.FC<{
  note: StaffNote;
  onUnpin: (noteId: string) => void;
}> = ({ note, onUnpin }) => {
  const [isHovered, setIsHovered] = useState(false);

  const userName = note.user
    ? `${note.user.first_name} ${note.user.last_name}`.trim() ||
      note.user.username
    : 'Unknown';

  const timeAgo = formatDistanceToNow(new Date(note.created_at), {
    addSuffix: true,
  });

  // Render content with @mentions stripped to plain text for compact view
  const renderCompactContent = (content: string) => {
    return content.replace(/@\[([^\]]+)\]\(([^)]+)\)/g, '@$1');
  };

  return (
    <div
      className="flex items-start gap-x-2 px-2.5 py-1.5 rounded-md hover:bg-zinc-50 transition-colors"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-x-1.5">
          <span className="text-[11px] font-semibold text-zinc-800">
            {userName}
          </span>
          <span className="text-[10px] font-medium text-zinc-500">
            {timeAgo}
          </span>
          {isHovered && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onUnpin(note.id)}
                  className="flex-none p-0.5 rounded hover:bg-zinc-200 transition-colors ml-auto"
                >
                  <PinOff className="h-3 w-3 text-zinc-500 hover:text-zinc-700" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={4}>
                <p className="text-[10px] font-medium">
                  Unpin for everyone on this matter
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <p className="text-xs font-semibold text-zinc-700 mt-0.5 whitespace-pre-wrap break-words leading-relaxed">
          {renderCompactContent(note.content)}
        </p>
      </div>
    </div>
  );
};

interface PinnedNotesBarProps {
  matterId: string;
  className?: string;
  onMinimize?: () => void;
}

export const PinnedNotesBar: React.FC<PinnedNotesBarProps> = ({
  matterId,
  className,
  onMinimize,
}) => {
  const { data: notes } = useGetStaffNotesQuery(
    { matterId },
    {
      pollingInterval: 5000,
      skipPollingIfUnfocused: true,
    },
  );

  const [togglePinStaffNote] = useTogglePinStaffNoteMutation();

  const pinnedNotes = (notes || [])
    .filter((n) => n.is_pinned)
    .sort(
      (a, b) =>
        new Date(b.pinned_at || b.created_at).getTime() -
        new Date(a.pinned_at || a.created_at).getTime(),
    );

  // Resizable height
  const [barHeight, setBarHeight] = useState(() => {
    const stored = localStorage.getItem(PINNED_BAR_HEIGHT_KEY);
    if (!stored) return PINNED_BAR_DEFAULT_HEIGHT;
    const parsed = parseInt(stored, 10);
    if (Number.isNaN(parsed)) return PINNED_BAR_DEFAULT_HEIGHT;
    return Math.min(Math.max(parsed, PINNED_BAR_MIN_HEIGHT), PINNED_BAR_MAX_HEIGHT);
  });
  const [isResizing, setIsResizing] = useState(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const currentHeightRef = useRef(barHeight);
  const cleanupResizeRef = useRef<(() => void) | null>(null);

  // Clean up resize listeners on unmount
  useEffect(() => {
    return () => {
      cleanupResizeRef.current?.();
    };
  }, []);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startYRef.current = e.clientY;
      startHeightRef.current = barHeight;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientY - startYRef.current;
        const newHeight = Math.min(
          Math.max(startHeightRef.current + delta, PINNED_BAR_MIN_HEIGHT),
          PINNED_BAR_MAX_HEIGHT,
        );
        currentHeightRef.current = newHeight;
        setBarHeight(newHeight);
      };

      const cleanup = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        cleanupResizeRef.current = null;
      };

      const handleMouseUp = () => {
        cleanup();

        // Persist final height from ref (avoids stale closure)
        localStorage.setItem(
          PINNED_BAR_HEIGHT_KEY,
          currentHeightRef.current.toString(),
        );
      };

      cleanupResizeRef.current = cleanup;
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    },
    [barHeight],
  );

  const handleUnpin = useCallback(
    async (noteId: string) => {
      try {
        await togglePinStaffNote({
          matterId,
          noteId,
          is_pinned: false,
        }).unwrap();
        toast.success('Note unpinned');
      } catch {
        toast.error('Failed to unpin note');
      }
    },
    [togglePinStaffNote, matterId],
  );

  if (pinnedNotes.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex flex-col border-b border-zinc-200 bg-amber-50/30 flex-shrink-0',
        className,
      )}
      style={{ height: barHeight }}
    >
      {/* Header — warm accent strip, the single bold design choice */}
      <div className="flex items-center gap-x-1.5 px-3 py-1.5 bg-amber-400/90">
        <Pin className="h-3 w-3 text-amber-900/70 fill-amber-900/70" />
        <span className="text-[10px] font-bold text-amber-950 uppercase tracking-wider">
          Pinned
        </span>
        <span className="text-[10px] font-semibold text-amber-800/70">
          · Visible to all
        </span>
        <span className="text-[10px] font-bold text-amber-900/60 ml-auto">
          {pinnedNotes.length}
        </span>
        {onMinimize && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onMinimize}
                className="p-0.5 rounded hover:bg-amber-500/30 transition-colors"
              >
                <ChevronRight className="h-3 w-3 text-amber-900/60" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={4}>
              <p className="text-[10px] font-medium">Minimize</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Notes list - scrollable */}
      <div className="flex-1 overflow-y-auto px-1">
        {pinnedNotes.map((note) => (
          <PinnedNoteItem key={note.id} note={note} onUnpin={handleUnpin} />
        ))}
      </div>

      {/* Resize handle */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        onMouseDown={handleResizeMouseDown}
        className="h-2 cursor-row-resize flex items-center justify-center flex-shrink-0 group"
      >
        <div
          className={cn(
            'w-8 h-0.5 rounded-full transition-colors',
            isResizing ? 'bg-zinc-400' : 'bg-zinc-200 group-hover:bg-zinc-300',
          )}
        />
      </div>
    </div>
  );
};

export default PinnedNotesBar;
